import React, { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { useLang } from "../i18n";
import { AttachmentInput } from "../components/FormLink";
import { calcAge } from "../components/SubjectRegistry";

const GENDERS = ["e.gender.m", "e.gender.f", "e.gender.o"];
const BLOOD = ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"];

const FIELDS = [
  { key: "name", label: "e.name", required: true },
  { key: "id_number", label: "e.id_number" },
  { key: "position", label: "e.position" },
  { key: "department", label: "e.department" },
  { key: "hire_date", label: "e.hire_date", type: "date" },
  { key: "nationality", label: "e.nationality" },
  { key: "birth_date", label: "e.birth_date", type: "date", required: true, age: true },
  { key: "medical_conditions", label: "e.medical_conditions", required: true },
];

export default function PublicKye({ publicToken }) {
  const { t } = useLang();
  const [form, setForm] = useState({ is_pep: false, allergic: false });
  const [atts, setAtts] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [police, setPolice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const onFile = (setter) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return setter(null);
    if (f.size > 1_500_000) return toast.error(t("k.file_optional"));
    const rd = new FileReader();
    rd.onload = () => setter({ filename: f.name, file_base64: rd.result });
    rd.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.allergic && !(form.allergy_detail || "").trim()) {
      toast.error(t("e.allergy_detail"));
      return;
    }
    setBusy(true);
    try {
      const attachments = [{ ...photo, note: "Foto" }, { ...police, note: "Récord Policivo" }, ...atts];
      const { data } = await api.post(`/public/form/${publicToken}`, { data: form, attachments });
      setDone(data.form_number);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="ec-card p-10 text-center space-y-3 max-w-xl mx-auto" data-testid="kye-public-done">
        <ShieldCheck className="w-12 h-12 text-emerald-600 mx-auto" />
        <h2 className="font-heading text-xl font-extrabold text-slate-900">{t("forms.public_done")}</h2>
        <p className="text-sm text-slate-600">{t("forms.public_done_no")} <span className="font-mono font-bold">{done}</span></p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="ec-card p-6 space-y-5 max-w-3xl mx-auto" data-testid="kye-public-form">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">{t("k.public_title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("k.public_sub")}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="ec-label">{t(f.label)}{f.required ? " *" : ""}</label>
            <input className="ec-input" type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={f.required} data-testid={`kye-field-${f.key}`} />
            {f.age && form.birth_date && <div className="text-xs text-slate-500 mt-1" data-testid="kye-age-hint">{t("e.age")}: {calcAge(form.birth_date)}</div>}
          </div>
        ))}
        <div>
          <label className="ec-label">{t("e.gender")} *</label>
          <select className="ec-input" value={form.gender || ""} onChange={(e) => setForm({ ...form, gender: e.target.value })} required data-testid="kye-field-gender">
            <option value="">—</option>
            {GENDERS.map((g) => <option key={g} value={t(g)}>{t(g)}</option>)}
          </select>
        </div>
        <div>
          <label className="ec-label">{t("e.blood_type")} *</label>
          <select className="ec-input" value={form.blood_type || ""} onChange={(e) => setForm({ ...form, blood_type: e.target.value })} required data-testid="kye-field-blood_type">
            <option value="">—</option>
            {BLOOD.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className="ec-label">{t("e.allergic")} *</label>
          <select className="ec-input" value={form.allergic ? "si" : "no"} onChange={(e) => setForm({ ...form, allergic: e.target.value === "si" })} data-testid="kye-field-allergic">
            <option value="no">{t("common.no")}</option>
            <option value="si">{t("common.yes")}</option>
          </select>
        </div>
        {form.allergic && (
          <div>
            <label className="ec-label">{t("e.allergy_detail")} *</label>
            <input className="ec-input" value={form.allergy_detail || ""} onChange={(e) => setForm({ ...form, allergy_detail: e.target.value })} required data-testid="kye-field-allergy_detail" />
          </div>
        )}
        <div>
          <label className="ec-label">{t("e.is_pep")}</label>
          <select className="ec-input" value={form.is_pep ? "si" : "no"} onChange={(e) => setForm({ ...form, is_pep: e.target.value === "si" })} data-testid="kye-field-is_pep">
            <option value="no">{t("common.no")}</option>
            <option value="si">{t("common.yes")}</option>
          </select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="ec-label">{t("e.photo")} *</label>
          <input className="ec-input" type="file" accept="image/*" required onChange={onFile(setPhoto)} data-testid="kye-photo-input" />
          {photo && <div className="text-xs font-semibold text-emerald-700 mt-1">{photo.filename}</div>}
        </div>
        <div>
          <label className="ec-label">{t("e.police_record")} *</label>
          <input className="ec-input" type="file" required onChange={onFile(setPolice)} data-testid="kye-police-input" />
          {police && <div className="text-xs font-semibold text-emerald-700 mt-1">{police.filename}</div>}
        </div>
      </div>
      <AttachmentInput value={atts} onChange={setAtts} testid="kye-attachments" />
      <button type="submit" disabled={busy} data-testid="kye-public-submit" className="w-full px-4 py-3 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 flex items-center justify-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("common.save")}
      </button>
    </form>
  );
}
